// Public recruitment-test page (Phase 4.1) — reached from the /assessment/:token
// link, no login required. The candidate answers multiple-choice questions and
// submits; the result is shown immediately. The answer key is never sent here.
import { useState, useEffect } from 'react';
import { api } from './api';

const C = { primary: '#DC0069', primaryDark: '#9A0048', text: '#111927', sub: '#6C737F', border: '#E5E7EB', bg: '#F9FAFB', green: '#0B815A', red: '#B42318' };

export default function PublicAssessment() {
  const token = decodeURIComponent(window.location.pathname.replace(/^\/assessment\//, ''));
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.publicAssessment(token).then(setData).catch((e) => setErr(e.message)); }, [token]);

  const submit = async () => {
    setBusy(true); setErr('');
    try { setData(await api.submitAssessment(token, answers)); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const wrap = { minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif', padding: 20 };
  const card = { width: '100%', maxWidth: 640, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 20, padding: 32, boxShadow: '0 20px 50px rgba(16,24,40,0.08)', margin: '32px 0' };

  if (err && !data) return <div style={wrap}><div style={{ ...card, textAlign: 'center', color: C.red, maxWidth: 460 }}>{err}</div></div>;
  if (!data) return <div style={wrap}><div style={{ ...card, maxWidth: 460 }}>Loading…</div></div>;

  const submitted = data.status === 'Submitted';
  const questions = data.questions || [];
  const allAnswered = questions.length > 0 && questions.every((q) => Number.isInteger(answers[q.id]));

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Augment Group · Assessment</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '8px 0 4px' }}>{data.test?.title}</h1>
        {!submitted && <p style={{ color: C.sub, fontSize: 14, marginBottom: 4 }}>Hi {data.candidateName}. {data.test?.description || 'Answer each question, then submit.'}</p>}
        {!submitted && <p style={{ color: C.sub, fontSize: 12.5, marginBottom: 20 }}>Suggested time: {data.test?.durationMins} min · Pass mark: {data.test?.passScore}%</p>}

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{data.passed ? '✅' : '📋'}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: data.passed ? C.green : C.text }}>{data.percent}%</div>
            <p style={{ color: C.sub, fontSize: 14, marginTop: 6 }}>You scored {data.score} of {data.maxScore}. {data.passed ? 'Thank you — our team will be in touch.' : 'Thank you for completing the assessment.'}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 18 }}>
              {questions.map((q, i) => (
                <div key={q.id} style={{ padding: 16, background: C.bg, borderRadius: 14 }}>
                  <p style={{ fontWeight: 600, color: C.text, fontSize: 14.5, marginBottom: 10 }}>{i + 1}. {q.prompt}</p>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {q.options.map((opt, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, border: `1px solid ${answers[q.id] === idx ? C.primary : C.border}`, background: answers[q.id] === idx ? '#fff' : '#fff', cursor: 'pointer', fontSize: 14, color: C.text }}>
                        <input type="radio" name={q.id} checked={answers[q.id] === idx} onChange={() => setAnswers({ ...answers, [q.id]: idx })} style={{ accentColor: C.primary }} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {err && <p style={{ color: C.red, fontSize: 13, margin: '14px 0 0' }}>{err}</p>}
            <button onClick={submit} disabled={busy || !allAnswered} style={{ marginTop: 20, width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: allAnswered ? C.primary : C.border, color: allAnswered ? '#fff' : C.sub, fontWeight: 700, cursor: allAnswered && !busy ? 'pointer' : 'default', fontSize: 14.5 }}>
              {busy ? 'Submitting…' : allAnswered ? 'Submit assessment' : `Answer all ${questions.length} questions`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
